import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPlansList } from '../../services/plans/listPlans';
import { createGrid } from '../../services/plans/createGrid';
import { analytics } from '../../utils/analytics';
import { Insurance } from '../../types/plans/types';
import {
  AgeRange,
  coerceRangeForBackend,
  isValidRange,
  reconstructAgeRange,
} from '../../utils/ageRanges';
import { getStatusConfig } from '../../utils/getStatusConfig';
import { AgeRangeSelect } from '../age-range-select';

interface PlanCredential {
  plan_id: string;
  planName: string;
  credentialStatus: string;
  value: string;
  ageRange: AgeRange;
  ageRangeLabel?: string;
  practiceId: string;
}

interface NewProviderInsuranceSetupProps {
  providerId: string;
  practiceId: string;
  practiceName: string;
  providerName: string;
  onComplete: () => void;
}

const COMBINED_STATUS_OPTIONS = [
  'Schedule Allowed',
  'Pending - Do Not Schedule',
  'Not Participating',
  'Established Patients Only',
  'PCP Match Required',
  'No Status',
];

const getCombinedStatusMapping = (combinedStatus: string) => {
  switch (combinedStatus) {
    case 'Schedule Allowed':
      return {
        credentialStatus: 'Credentialed',
        value: 'Schedule Allowed',
      };
    case 'Pending - Do Not Schedule':
      return { credentialStatus: 'Pending', value: 'No Status' };
    case 'Not Participating':
      return {
        credentialStatus: 'Credentialed',
        value: 'Not Participating',
      };
    case 'Established Patients Only':
      return {
        credentialStatus: 'Credentialed',
        value: 'Established Patients',
      };
    case 'PCP Match Required':
      return { credentialStatus: 'Credentialed', value: 'PCP Match' };
    case 'No Status':
      return { credentialStatus: 'Credentialed', value: 'No Status' };
    default:
      return { credentialStatus: 'Credentialed', value: 'No Status' };
  }
};

const getCombinedStatusFromFields = (
  credentialStatus: string,
  value: string
) => {
  if (credentialStatus?.toLowerCase() === 'pending') {
    return 'Pending - Do Not Schedule';
  }

  const valueLower = value?.toLowerCase();

  switch (valueLower) {
    case 'schedule allowed':
      return 'Schedule Allowed';
    case 'not participating':
      return 'Not Participating';
    case 'established patients':
      return 'Established Patients Only';
    case 'pcp match':
      return 'PCP Match Required';
    case 'no status':
      return 'No Status';
    default:
      return 'No Status';
  }
};

const getStatusConfigForOption = (combinedStatus: string) => {
  const mapping = getCombinedStatusMapping(combinedStatus);
  // Create a minimal Insurance object for getStatusConfig
  const dummyInsurance: Insurance = {
    id: '',
    emrInsurancePlan: null,
    friendlyName: null,
    companyName: null,
    va: false,
    hmo: false,
    medicareAdvantage: false,
    commercial: false,
    primaryPlan: false,
    secondaryPlan: false,
    marketplace: false,
    startDate: null,
    endDate: null,
  };
  return getStatusConfig(
    mapping.value,
    dummyInsurance,
    undefined,
    mapping.credentialStatus
  );
};

const NewProviderInsuranceSetup: React.FC<NewProviderInsuranceSetupProps> = ({
  providerId,
  practiceId,
  practiceName,
  providerName,
  onComplete,
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [planCredentials, setPlanCredentials] = useState<Map<string, PlanCredential>>(new Map());
  const [dirtyPlanIds, setDirtyPlanIds] = useState<Set<string>>(new Set());
  const [selectedPlanIds, setSelectedPlanIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState<'ascending' | 'descending'>('ascending');
  const [isSaving, setIsSaving] = useState(false);

  // Fetch plans from practice
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const result = await getPlansList(null, {
          practiceIds: [practiceId],
        }, 1000);

        if (result.data?.insurancePlans) {
          // Initialize plan credentials with default values
          const credentialsMap = new Map<string, PlanCredential>();
          const uniqueInsuranceIds = new Set<string>();
          
          for (const insurancePlan of result.data.insurancePlans) {
            for (const plan of insurancePlan.plans) {
              const insuranceId = plan.insurance.id;
              if (!uniqueInsuranceIds.has(insuranceId)) {
                uniqueInsuranceIds.add(insuranceId);
                
                const ageRange = reconstructAgeRange({
                  minimumAge: plan.minimumAge,
                  maximumAge: plan.maximumAge,
                  age: plan.age,
                  metadata: plan.metadata as {
                    minimumAge?: number | null;
                    maximumAge?: number | null;
                  } | undefined,
                });

                credentialsMap.set(insuranceId, {
                  plan_id: insuranceId,
                  planName: plan.insurance.friendlyName || plan.insurance.companyName || 'Unknown Plan',
                  credentialStatus: plan.credentialstatus || 'Credentialed',
                  value: plan.value || 'No Status',
                  ageRange,
                  practiceId,
                });
              }
            }
          }
          
          setPlanCredentials(credentialsMap);
          setSelectedPlanIds(new Set(credentialsMap.keys()));
        }
      } catch (error) {
        toast({
          title: 'Error fetching plans',
          description: 'Failed to load insurance plans. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [practiceId, toast]);

  // Filter and sort plans
  const filteredAndSortedPlans = useMemo(() => {
    let filtered = Array.from(planCredentials.values());

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter((cred) =>
        cred.planName.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort by plan name
    filtered.sort((a, b) => {
      const nameA = a.planName.toUpperCase();
      const nameB = b.planName.toUpperCase();
      return sortOrder === 'ascending'
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    });

    return filtered;
  }, [planCredentials, searchQuery, sortOrder]);

  // Handle status change
  const handleStatusChange = useCallback((planId: string, combinedStatus: string) => {
    const mapping = getCombinedStatusMapping(combinedStatus);
    setPlanCredentials((prev) => {
      const newMap = new Map(prev);
      const cred = newMap.get(planId);
      if (cred) {
        newMap.set(planId, {
          ...cred,
          credentialStatus: mapping.credentialStatus,
          value: mapping.value,
        });
      }
      return newMap;
    });
    setDirtyPlanIds((prev) => new Set(prev).add(planId));
  }, []);

  // Handle age range change
  const handleAgeRangeChange = useCallback((planId: string, ageRange: AgeRange, label?: string) => {
    setPlanCredentials((prev) => {
      const newMap = new Map(prev);
      const cred = newMap.get(planId);
      if (cred) {
        newMap.set(planId, {
          ...cred,
          ageRange,
          ageRangeLabel: label,
        });
      }
      return newMap;
    });
    setDirtyPlanIds((prev) => new Set(prev).add(planId));
  }, []);

  // Select all filtered plans
  const allFilteredSelected =
    filteredAndSortedPlans.length > 0 &&
    filteredAndSortedPlans.every((cred) => selectedPlanIds.has(cred.plan_id));
  const someFilteredSelected =
    filteredAndSortedPlans.length > 0 &&
    filteredAndSortedPlans.some((cred) => selectedPlanIds.has(cred.plan_id));

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedPlanIds((prev) => {
          const next = new Set(prev);
          filteredAndSortedPlans.forEach((cred) => next.add(cred.plan_id));
          return next;
        });
      } else {
        setSelectedPlanIds((prev) => {
          const next = new Set(prev);
          filteredAndSortedPlans.forEach((cred) => next.delete(cred.plan_id));
          return next;
        });
      }
    },
    [filteredAndSortedPlans]
  );

  const handleRowSelect = useCallback((planId: string, checked: boolean) => {
    setSelectedPlanIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(planId);
      else next.delete(planId);
      return next;
    });
  }, []);

  // Handle save
  const handleSave = useCallback(async () => {
    if (selectedPlanIds.size === 0) {
      toast({
        title: 'No plans selected',
        description: 'Select at least one plan to copy to this provider.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    try {
      const gridEntries = Array.from(selectedPlanIds).map((planId) => {
        const cred = planCredentials.get(planId);
        if (!cred) throw new Error(`Plan credential not found for ${planId}`);

        if (!isValidRange(cred.ageRange)) {
          throw new Error(`Invalid age range for ${cred.planName}`);
        }

        const { minimumAge, maximumAge } = coerceRangeForBackend(cred.ageRange);

        return {
          practiceId: cred.practiceId,
          providerId,
          plan_id: cred.plan_id,
          credentialStatus: cred.credentialStatus,
          value: cred.value,
          minimumAge,
          maximumAge,
        };
      });

      await createGrid({ gridEntries });

      analytics.trackEvent('create', 'insurance');

      toast({
        title: 'Insurance plans saved successfully',
        description: `Saved ${gridEntries.length} insurance plan(s) for ${providerName}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Navigate to provider detail page
      navigate(`/providers/${providerId}`);
      onComplete();
    } catch (error) {
      toast({
        title: 'Error saving plans',
        description: error instanceof Error ? error.message : 'Failed to save insurance plans. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  }, [selectedPlanIds, planCredentials, providerId, providerName, navigate, onComplete, toast]);

  // Handle skip
  const handleSkip = useCallback(() => {
    navigate(`/providers/${providerId}`);
    onComplete();
  }, [navigate, providerId, onComplete]);

  if (loading) {
    return (
      <Modal isOpen={true} onClose={() => {}} size="full" closeOnOverlayClick={false} closeOnEsc={false}>
        <ModalOverlay>
          <ModalContent>
            <ModalBody>
              <Flex justify="center" align="center" minH="50vh">
                <VStack spacing={4}>
                  <Spinner size="xl" />
                  <Text>Loading insurance plans...</Text>
                </VStack>
              </Flex>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      </Modal>
    );
  }

  return (
    <Modal isOpen={true} onClose={() => {}} size="full" closeOnOverlayClick={false} closeOnEsc={false}>
      <ModalOverlay>
        <ModalContent maxW="90vw" maxH="90vh">
          <ModalHeader fontSize="lg" fontWeight="bold">
            Insurance Plans - {providerName}
          </ModalHeader>
          <ModalBody overflowY="auto" maxH="calc(90vh - 200px)">
            <VStack spacing={4} align="stretch">
              <Text fontSize="0.875rem" color="gray.600" lineHeight="1.4">
                <strong>These plans will be copied to {providerName}.</strong> Edit insurance plan status and age ranges for each plan. The status dropdown combines both credential status and patient acceptance into a single, clear selection. Make multiple changes and save them all at once using the save button below.
              </Text>
              <Text fontSize="0.875rem" color="gray.600" lineHeight="1.4">
                <strong>How to edit:</strong> Change any dropdown values below. Modified rows will show "Modified" status. Use the "Save All Changes" button to save all your changes at once.
                <br />
                <strong>Note:</strong> The "Status" dropdown combines both credential status and patient acceptance into a single, clear selection.
              </Text>

              {/* Search and Sort Controls */}
              <Flex justify="space-between" align="center" gap="1rem">
                <InputGroup maxW="24rem">
                  <InputLeftElement pointerEvents="none">
                    <Text color="gray.400">🔍</Text>
                  </InputLeftElement>
                  <Input
                    placeholder="Search plans or practice"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </InputGroup>

                <Select
                  bg="#F7FAFC"
                  height="2.5rem"
                  fontSize="0.875rem"
                  _hover={{ bg: 'white' }}
                  borderRadius="0.375rem"
                  border="1px solid transparent"
                  value={sortOrder}
                  onChange={(e) =>
                    setSortOrder(e.target.value as 'ascending' | 'descending')
                  }
                >
                  <option value="ascending">Plan Name A - Z</option>
                  <option value="descending">Plan Name Z - A</option>
                </Select>
              </Flex>

              {/* Plans Table */}
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th width="40px" px={2}>
                      <Checkbox
                        isChecked={allFilteredSelected}
                        isIndeterminate={someFilteredSelected && !allFilteredSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        aria-label="Select all plans"
                      />
                    </Th>
                    <Th>PRACTICE</Th>
                    <Th>PLAN NAME</Th>
                    <Th>STATUS</Th>
                    <Th>AGE RANGE</Th>
                    <Th>STATUS</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredAndSortedPlans.length > 0 ? (
                    filteredAndSortedPlans.map((cred) => {
                      const isDirty = dirtyPlanIds.has(cred.plan_id);
                      const combinedStatus = getCombinedStatusFromFields(
                        cred.credentialStatus,
                        cred.value
                      );
                      const statusConfig = getStatusConfigForOption(combinedStatus);

                      return (
                        <Tr key={cred.plan_id}>
                          <Td width="40px" px={2}>
                            <Checkbox
                              isChecked={selectedPlanIds.has(cred.plan_id)}
                              onChange={(e) => handleRowSelect(cred.plan_id, e.target.checked)}
                              aria-label={`Select ${cred.planName}`}
                            />
                          </Td>
                          <Td>{practiceName}</Td>
                          <Td fontWeight="500">{cred.planName}</Td>
                          <Td>
                            <Menu>
                              <MenuButton
                                as={Button}
                                size="sm"
                                bg={statusConfig.bgColor}
                                color="white"
                                _hover={{ bg: statusConfig.bgColor, opacity: 0.9 }}
                                minW="200px"
                                maxW="300px"
                                width="fit-content"
                                textAlign="left"
                                rightIcon={<ChevronDownIcon color="white" boxSize={4} />}
                                data-testid="status-dropdown-button"
                              >
                                {statusConfig.label}
                              </MenuButton>
                              <MenuList zIndex={1500}>
                                {COMBINED_STATUS_OPTIONS.map((option) => {
                                  const optionConfig = getStatusConfigForOption(option);
                                  return (
                                    <MenuItem
                                      key={option}
                                      onClick={() => handleStatusChange(cred.plan_id, option)}
                                      bg={option === combinedStatus ? 'blue.50' : 'white'}
                                    >
                                      <Flex align="center" gap={2}>
                                        <Box
                                          w="12px"
                                          h="12px"
                                          borderRadius="50%"
                                          bg={optionConfig.bgColor}
                                        />
                                        <VStack align="start" spacing={0}>
                                          <Text fontSize="sm" fontWeight="600">
                                            {optionConfig.label}
                                          </Text>
                                          <Text fontSize="xs" color="gray.600">
                                            {optionConfig.helperText.split(' | ')[0]}
                                          </Text>
                                        </VStack>
                                      </Flex>
                                    </MenuItem>
                                  );
                                })}
                              </MenuList>
                            </Menu>
                          </Td>
                          <Td>
                            <Box maxW="300px">
                              <AgeRangeSelect
                                value={cred.ageRange}
                                onChange={(range, label) =>
                                  handleAgeRangeChange(cred.plan_id, range, label)
                                }
                              />
                            </Box>
                          </Td>
                          <Td>
                            {isDirty ? (
                              <Text fontSize="sm" color="orange.500" fontWeight="600">
                                Modified
                              </Text>
                            ) : (
                              <Text fontSize="sm" color="green.500" fontWeight="600">
                                Saved
                              </Text>
                            )}
                          </Td>
                        </Tr>
                      );
                    })
                  ) : (
                    <Tr>
                      <Td colSpan={6} textAlign="center" py={8}>
                        <Text color="gray.500">
                          {searchQuery ? 'No plans found matching your search.' : 'No insurance plans found in this practice.'}
                        </Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>

              {filteredAndSortedPlans.length > 0 && (
                <Text fontSize="0.875rem" color="gray.600" textAlign="center">
                  Showing {filteredAndSortedPlans.length} of {planCredentials.size} plan{planCredentials.size !== 1 ? 's' : ''}
                </Text>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={4}>
              <Button
                variant="outline"
                onClick={handleSkip}
                isDisabled={isSaving}
              >
                Skip
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSave}
                isLoading={isSaving}
                loadingText="Saving..."
                isDisabled={selectedPlanIds.size === 0 || isSaving}
              >
                Save All Changes ({selectedPlanIds.size})
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};

export default NewProviderInsuranceSetup;
